"use client";

import React from "react";
import { PhoneCall, Clock, ShieldAlert, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const guarantees = [{
  id: "g1",
  label: "Under 45-Min Local Arrival"
}, {
  id: "g2",
  label: "24/7 Live Emergency Dispatch"
}, {
  id: "g3",
  label: "Upfront Pricing & No Hidden Fees"
}, {
  id: "g4",
  label: "Licensed & Insured Jonesboro Techs"
}];
export default function Emergencycta() {
  const prefersReduced = useReducedMotion();
  return <section id="emergencyservicecta" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={{
        opacity: 0,
        y: prefersReduced ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="relative overflow-hidden rounded-3xl bg-primary text-primary-foreground shadow-2xl p-8 sm:p-12 lg:p-16 border border-primary/20">
          {/* Decorative ambient background accents */}
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-accent/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-20 -bottom-20 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content Area */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="bg-accent text-accent-foreground border-accent font-semibold px-3 py-1 text-xs sm:text-sm uppercase tracking-wide gap-1.5">
                  <ShieldAlert className="size-4" />
                  Urgent Plumbing Help
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-primary-foreground/90 bg-primary-foreground/10 px-3 py-1 rounded-md">
                  <Clock className="size-3.5 text-accent" />
                  Available 24 Hours a Day, 7 Days a Week
                </span>
              </div>

              <div className="space-y-3">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-default font-bold tracking-tight text-primary-foreground leading-tight">
                  Plumbing Crisis in Jonesboro? We Are on the Way.
                </h2>
                <p className="text-base sm:text-lg text-primary-foreground/80 max-w-2xl font-sans leading-relaxed">
                  Burst pipes, severe sewer backups, or failing water heaters. Speak directly with our on-duty emergency technicians for fast, reliable repair.
                </p>
              </div>

              {/* Bullet points */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {guarantees.map((item, index) => <div key={item.id} data-index={index} className="flex items-center gap-2.5 text-sm sm:text-base text-primary-foreground/90">
                    <CheckCircle2 className="size-5 text-accent shrink-0" />
                    <span>{item.label}</span>
                  </div>)}
              </div>
            </div>

            {/* Right Action Area */}
            <div className="lg:col-span-5 flex flex-col justify-center">
              <div className="rounded-2xl bg-primary-foreground/10 p-6 sm:p-8 backdrop-blur-md border border-primary-foreground/15 text-center sm:text-left space-y-6">
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent block mb-1">
                    Immediate Response Line
                  </span>
                  <p className="text-xs text-primary-foreground/75 mb-3">
                    Tap below to call our 24/7 on-call dispatcher
                  </p>
                  <a href="tel:4708479076" className="group inline-flex items-center justify-center sm:justify-start gap-3 text-2xl sm:text-3xl font-extrabold text-primary-foreground hover:text-accent transition-colors w-full" aria-label="Call emergency plumbing dispatcher at (470) 847-9076">
                    <div className="size-12 rounded-full bg-accent text-accent-foreground flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform">
                      <PhoneCall className="size-6 animate-pulse" />
                    </div>
                    <span>(470) 847-9076</span>
                  </a>
                </div>

                <div className="h-px w-full bg-primary-foreground/15" />

                <div className="space-y-3">
                  <span className="text-xs text-primary-foreground/70 block">
                    Need non-emergency scheduling or a detailed estimate?
                  </span>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold w-full justify-center shadow-lg">
                      <Link to="#leak-detection-service-request-form">
                        <span>Request Service Online</span>
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>
                    <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground w-full justify-center">
                      <Link to="/services/">
                        <span>All Services</span>
                      </Link>
                    </Button>
                  </div>
                </div>

                <div className="pt-1 flex items-center justify-center sm:justify-start gap-2 text-xs text-primary-foreground/75">
                  <Sparkles className="size-3.5 text-accent shrink-0" />
                  <span>Serving Jonesboro, Clayton County & Surrounding Areas</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}